<template>
  <div class="flex flex-col h-full px-6 bg-gray-100 lg:px-12">
    <div class="flex-grow w-full max-w-lg mx-auto space-y-6">
      <div class="flex items-center justify-between py-5 border-b border-gray-300 lg:py-7">
        <div class="text-lg leading-tight">
          <div class="font-bold">
            Booking Summary
          </div>
          <div class="text-gray-500">
            {{ options.account }}
          </div>
        </div>
        <div class="hidden lg:block">
          <img :src="options.logo" v-if="options.logo" :alt="options.account" class="w-auto h-10">
        </div>
      </div>

      <Collapse :active="form.origins.length > 0" :duration="200">
        <div class="flex items-center justify-between h-6 text-xs font-bold tracking-wider text-gray-500 uppercase">
          Pickups
          <router-link :to="{ name: 'BookOrigin' }" class="px-2 text-xs font-medium tracking-wider text-gray-500 uppercase bg-white bg-opacity-25 rounded-full hover:no-underline hover:bg-opacity-75 focus:bg-gray-200" v-if="form.destinations.length < 2 && options.allow_multiple_origins">
            <fa :icon="['far', 'plus']" /> Add
          </router-link>
        </div>
        <div class="mt-2 bg-white border rounded-md shadow-sm">
          <div class="p-5 text-sm">
            <div v-for="(origin, index) in form.origins" :key="origin.id">
              <hr class="my-4" v-if="index !== 0">
              <div class="flex flex-col justify-between xl:items-center xl:flex-row">
                <div class="pr-3">
                  <div class="font-medium">{{ origin.name || origin.contact_name || 'No Contact Set' }}</div>
                  <div class="max-w-xs text-gray-500 truncate">{{ origin.formatted_address || 'No Address Set' }}</div>
                  <div class="flex items-center mt-0 divide-x divide-gray-300" v-if="addressTotals.origins[index].total">
                    <div class="pr-2 text-xs text-gray-700">
                      <currency :value="addressTotals.origins[index].total" :currency-code="portal.currency" :subunits-value="true" />
                    </div>
                    <div class="px-2 text-xs text-gray-500" v-if="addressTotals.origins[index].products">
                      {{ addressTotals.origins[index].products }} {{ addressTotals.origins[index].products | pluralize('Item') }}
                    </div>
                    <div class="px-2 text-xs text-gray-500" v-if="addressTotals.origins[index].addons">
                      {{ addressTotals.origins[index].addons }} {{ addressTotals.origins[index].addons | pluralize('Addon') }}
                    </div>
                  </div>
                </div>
                <div class="flex mt-1 -ml-1.5 xl:ml-0 space-x-4 xl:mt-0">
                  <button class="px-1.5 py-0.5 font-medium rounded-full text-accent focus:ring-accent hover:bg-accent hover:bg-opacity-10 hover:no-underline" @click="changeAddress({ type: 'origins', index })">
                    Change
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Collapse>
      <Collapse :active="form.destinations.length > 0" :duration="200">
        <div class="flex items-center justify-between h-6 text-xs font-bold tracking-wider text-gray-500 uppercase">
          Deliveries
          <router-link :to="{ name: 'BookDestination' }" class="px-2 text-xs font-medium tracking-wider text-gray-500 uppercase bg-white bg-opacity-25 rounded-full hover:no-underline hover:bg-opacity-75 focus:bg-gray-200" v-if="form.origins.length < 2 && options.allow_multiple_destinations">
            <fa :icon="['far', 'plus']" /> Add
          </router-link>
        </div>
        <div class="mt-2 bg-white border rounded-md shadow-sm">
          <div class="p-5 text-sm">
            <div v-for="(destination, index) in form.destinations" :key="destination.id">
              <hr class="my-4" v-if="index !== 0">
              <div class="flex flex-col justify-between xl:items-center xl:flex-row">
                <div class="pr-3">
                  <div class="font-medium">{{ destination.name || destination.contact_name || 'No Contact Set' }}</div>
                  <div class="max-w-xs text-gray-500 truncate">{{ destination.formatted_address || 'No Address Set' }}</div>
                  <div class="inline-flex items-center py-1 mt-2 leading-none divide-x divide-gray-300 rounded-md" v-if="addressTotals.destinations[index].total">
                    <div class="pr-2 text-xs text-gray-700">
                      <currency :value="addressTotals.destinations[index].total" :currency-code="portal.currency" :subunits-value="true" />
                    </div>
                    <div class="px-2 text-xs text-gray-500" v-if="addressTotals.destinations[index].products">
                      {{ addressTotals.destinations[index].products }} {{ addressTotals.destinations[index].products | pluralize('Item') }}
                    </div>
                    <div class="px-2 text-xs text-gray-500" v-if="addressTotals.destinations[index].addons">
                      {{ addressTotals.destinations[index].addons }} {{ addressTotals.destinations[index].addons | pluralize('Addon') }}
                    </div>
                  </div>
                </div>
                <div class="flex mt-1 -ml-1.5 xl:ml-0 space-x-4 xl:mt-0">
                  <button class="px-1.5 py-0.5 font-medium rounded-full text-accent focus:ring-accent hover:bg-accent hover:bg-opacity-10 hover:no-underline" @click="changeAddress({ type: 'destinations', index })">
                    Change
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Collapse>
      <Collapse :active="form.date && form.date.valueOf() > 0" :duration="200">
        <div class="h-6 text-xs font-bold tracking-wider text-gray-500 uppercase">
          Date <span v-if="form.time">&amp; Time</span>
        </div>
        <div class="p-5 mt-2 bg-white border rounded-md shadow-sm">
          <div class="items-center text-sm lg:flex">
            <div class="flex-grow">
              <div class="w-full max-w-xs font-medium truncate">
                {{ form.date | moment('ddd, Do MMMM YYYY') }}
              </div>
            </div>
            <div class="flex-shrink-0">
              <router-link :to="{ name: 'BookDate', params: { id: $route.params.id } }" class="px-1.5 py-0.5 font-medium rounded-full text-accent focus:ring-accent hover:bg-accent hover:bg-opacity-10 hover:no-underline">
                Change
              </router-link>
            </div>
          </div>
          <hr class="my-4" v-if="form.time">
          <div class="items-center text-sm lg:flex" v-if="form.time">
            <div class="flex-grow">
              <div class="w-full max-w-xs text-gray-500 truncate">
                From {{ form.time }}
              </div>
            </div>
            <div class="flex-shrink-0">
              <router-link :to="{ name: 'BookTime', params: { id: $route.params.id } }" class="px-1.5 py-0.5 font-medium rounded-full text-accent focus:ring-accent hover:bg-accent hover:bg-opacity-10 hover:no-underline">
                Change
              </router-link>
            </div>
          </div>
        </div>
      </Collapse>
    </div>

    <div class="flex flex-col items-center justify-between flex-none w-full max-w-lg gap-6 pt-8 mx-auto lg:flex-row pb-28 lg:py-8 bg-gradient-to-t from-gray-100 to-transparent">
      <div class="flex gap-4">
        <button class="text-xs text-gray-500 hover:underline" @click="startOver">
          Start Over
        </button>
        <template v-if="options.legal_terms">
          <div class="w-px h-4 mx-3 bg-gray-300"></div>
          <a :href="options.legal_terms" target="_blank" rel="noopener nofolower noreferrer" class="text-xs text-gray-500">
            Terms
          </a>
        </template>
        <template v-if="options.legal_privacy">
          <div class="w-px h-4 mx-3 bg-gray-300"></div>
          <a :href="options.legal_privacy" target="_blank" rel="noopener nofolower noreferrer" class="text-xs text-gray-500">
            Privacy
          </a>
        </template>
      </div>
      <a href="https://www.fleetbeacon.app?ref=booking" target="_blank" class="flex items-center text-xs text-right text-gray-500 hover:no-underline">
        Powered by <span class="sr-only">Fleetbeacon</span>
        <img src="@/assets/img/logo-dark.svg" class="h-4 ml-2 opacity-75 hover:opacity-100">
      </a>
    </div>

    <Modal title="Change Address" :open.sync="changeAddressModal" size="lg:max-w-xl">
      <div class="p-6">
        <!-- Help Text -->
        <BookHelpBubble
          v-if="changeAddressType === 'origins' ? options.origin_help_text : options.destination_help_text"
          :content="changeAddressType === 'origins' ? options.origin_help_text : options.destination_help_text" 
        />

        <!-- Address Form -->
        <BookAddressForm
          ref="addressForm"
          :address-type="changeAddressType"
          :address-index="changeAddressIndex"
          @valid="saveAddress" 
        />
        
        <div class="flex items-center justify-between mt-6">
          <button class="btn btn-accent" @click="$refs.addressForm.submit()">
            Save Changes
          </button>
          <button v-if="addressIsRemoveable" class="font-medium text-red-500" @click="removeAddress({ type: changeAddressType, index: changeAddressIndex }); changeAddressModal = false">
            Remove
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import BookAddressForm from './BookAddressForm'
import BookHelpBubble from './BookHelpBubble'

export default {
  name: 'BookSummary',

  components: {
    BookAddressForm,
    BookHelpBubble
  },

  computed: {
    ...mapState(['portal']),
    ...mapState('booking', ['form', 'options']),
    ...mapGetters('booking', ['addressTotals']),

    addressIsRemoveable () {
      if (this.changeAddressType) {
        return this.form[this.changeAddressType].length > 1
      } else {
        return false
      }
    }
  },

  data () {
    return {
      changeAddressModal: false,
      changeAddressTab: 'address',
      changeAddressType: null,
      changeAddressIndex: null
    }
  },

  methods: {
    ...mapMutations('booking', ['removeAddress', 'updateAddress', 'resetForm']),

    changeAddress ({ type, index }) {
      this.changeAddressType = type
      this.changeAddressIndex = index
      this.changeAddressModal = true
    },

    saveAddress (address) {
      this.changeAddressType = null
      this.changeAddressIndex = null
      this.changeAddressModal = false
      // this.updateAddress({ 
      //   type: this.changeAddressType,
      //   index: this.changeAddressIndex,
      //   address 
      // })
    },

    startOver () {
      if (window.confirm(`Are you sure you want to start over? You will clear all the information you've entered into the form. This cannot be undone.`)) {
        this.resetForm()
        if (this.$route.name !== 'BookDetails') {
          this.$router.push({ 
            name: 'BookDetails', 
            params: { ...this.$route.params } 
          })
        }
      }
    }
  }
}
</script>